/**
 * Payslips — generate them, read them, print them.
 * Route: /payslips
 * @file src/web/features/payslips/pages/index.tsx
 *
 * @llm-rule WHEN: A Company Admin (or a Head they have granted) issues pay, or
 *   anyone looks at their own
 * @llm-rule NOTE: Two audiences on one page. Whoever may manage payslips gets
 *   the generator and the whole company's list; everyone else gets their own,
 *   read-only. The server enforces this independently — the UI only decides
 *   what to render.
 * @llm-rule AVOID: Doing money arithmetic in floats. The server stores integer
 *   minor units; this page only ever divides by 100 to display.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Skeleton, toast, useConfirm } from '@bloomneo/uikit';
import { Download, FileText, IndianRupee, Plus, Trash2 } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel, SectionLabel } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import {
  canManagePayslips,
  DEFAULT_CAPABILITIES,
  describeRole,
  type CapabilityMap,
  type HeadDesignation,
  type OrgRole,
} from '../../../shared/domain';

interface PayslipPerson {
  id: string;
  name: string | null;
  email: string;
  title: string | null;
  orgRole: OrgRole;
}

interface Payslip {
  id: string;
  month: string;
  grossMinor: number;
  gstMinor: number;
  pfMinor: number;
  netMinor: number;
  currency: string;
  user: PayslipPerson;
}

interface Person {
  id: string;
  name: string | null;
  email: string;
  orgRole: OrgRole;
  headDesignation?: HeadDesignation | null;
}

/** Minor units to a readable amount. Never used for arithmetic. */
function money(minor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

function monthLabel(iso: string): string {
  // The column is a DATE at UTC midnight; format in UTC or it slips a month.
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Pay is never issued in advance, so the picker stops at this month. */
const CURRENT_MONTH = new Date().toISOString().slice(0, 7);

const EMPTY = {
  userId: '',
  basis: 'yearly' as 'yearly' | 'monthly',
  amount: '',
  gstPercent: '',
  pfPercent: '',
  fromMonth: '',
  toMonth: '',
};

function PayslipsPage() {
  const { user } = useAuth();
  const { request } = useAppRequest();
  const confirm = useConfirm();

  const role = (user?.orgRole ?? 'EMPLOYEE') as OrgRole;
  // The company's delegation map decides whether this rank may generate. The
  // server checks the same thing — this only picks what to render.
  const [capabilities, setCapabilities] = useState<CapabilityMap>(DEFAULT_CAPABILITIES);
  const manages = canManagePayslips(role, capabilities, Boolean(user?.canAccessPayslips));

  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [companyName, setCompanyName] = useState('');

  const load = useCallback(async () => {
    const result = await request<{ payslips: Payslip[] }>('/api/payslip');
    if (result.ok) setPayslips(result.data.payslips);
    else toast.error(result.error);
    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void request<{ capabilities: CapabilityMap }>('/api/org/capabilities').then((r) => {
      if (r.ok) setCapabilities(r.data.capabilities);
    });
    // The company name heads every printed payslip.
    void request<{ organisation: { name: string } | null }>('/api/organisation/mine').then((r) => {
      if (r.ok && r.data.organisation) setCompanyName(r.data.organisation.name);
    });
  }, [request]);

  function toggleOne(id: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function toggleMany(ids: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  const chosen = useMemo(() => payslips.filter((p) => selected.has(p.id)), [payslips, selected]);
  const allIds = useMemo(() => payslips.map((p) => p.id), [payslips]);
  const allChosen = allIds.length > 0 && allIds.every((id) => selected.has(id));

  useEffect(() => {
    if (!manages) return;
    void request<{ people: Person[] }>('/api/org/people').then((r) => {
      if (r.ok) setPeople(r.data.people);
    });
  }, [manages, request]);

  async function generate() {
    if (!form.userId) return toast.error('Choose whose payslips these are');
    if (!form.amount.trim()) return toast.error('Enter the package or monthly amount');
    if (!form.fromMonth || !form.toMonth) return toast.error('Give a month range');
    if (form.fromMonth > CURRENT_MONTH || form.toMonth > CURRENT_MONTH) {
      return toast.error('Payslips cannot be generated for a future month');
    }

    setBusy(true);
    try {
      const amount = Number(form.amount);
      const result = await request<{ message: string }>('/api/payslip', {
        method: 'POST',
        body: {
          userId: form.userId,
          ...(form.basis === 'yearly' ? { yearlyPackage: amount } : { monthlyAmount: amount }),
          gstPercent: form.gstPercent ? Number(form.gstPercent) : 0,
          pfPercent: form.pfPercent ? Number(form.pfPercent) : 0,
          fromMonth: form.fromMonth,
          toMonth: form.toMonth,
        },
      });

      if (!result.ok) return toast.error(result.error);
      toast.success(result.data.message);
      setForm(EMPTY);
      void load();
    } finally {
      setBusy(false);
    }
  }

  async function remove(slip: Payslip) {
    const ok = await confirm({
      title: 'Delete this payslip?',
      description: `${monthLabel(slip.month)} for ${slip.user.name ?? slip.user.email}. This cannot be undone.`,
      confirmLabel: 'Delete',
takeover: true,
    } as never);
    if (!ok) return;

    const result = await request(`/api/payslip/${slip.id}`, { method: 'DELETE' });
    if (!result.ok) return toast.error(result.error);
    toast.success('Payslip deleted');
    void load();
  }

  /** Grouped by person so a company list stays readable. */
  const grouped = useMemo(() => {
    const map = new Map<string, { person: PayslipPerson; slips: Payslip[] }>();
    for (const slip of payslips) {
      const entry = map.get(slip.user.id) ?? { person: slip.user, slips: [] };
      entry.slips.push(slip);
      map.set(slip.user.id, entry);
    }
    return [...map.values()];
  }, [payslips]);

  return (
    <AppShell>
      <div className="flex flex-col gap-5 print:hidden">
        <div>
          <h1 className="text-[26px] font-semibold tracking-tight text-ink">Payslips</h1>
          <p className="mt-1 text-[13.5px] text-ink-soft">
            {manages
              ? 'Generate a run of payslips for anyone in the company. Everyone can always read their own.'
              : 'Your pay record, month by month.'}
          </p>
        </div>

        {manages && (
          <Panel>
            <SectionLabel>Generate</SectionLabel>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-soft">Person</span>
                <select
                  className="h-9 rounded-[10px] border border-line bg-paper px-2.5 text-[13.5px] text-ink"
                  value={form.userId}
                  onChange={(e) => setForm({ ...form, userId: e.target.value })}
                >
                  <option value="">Choose someone…</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name ?? p.email} · {describeRole(p.orgRole, p.headDesignation)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-soft">Basis</span>
                <select
                  className="h-9 rounded-[10px] border border-line bg-paper px-2.5 text-[13.5px] text-ink"
                  value={form.basis}
                  onChange={(e) =>
                    setForm({ ...form, basis: e.target.value as 'yearly' | 'monthly' })
                  }
                >
                  <option value="yearly">Yearly package</option>
                  <option value="monthly">Monthly amount</option>
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-soft">
                  {form.basis === 'yearly' ? 'Yearly package' : 'Monthly amount'}
                </span>
                <Input
                  type="number"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder={form.basis === 'yearly' ? '720000' : '60000'}
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12px] font-medium text-ink-soft">GST %</span>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={form.gstPercent}
                    onChange={(e) => setForm({ ...form, gstPercent: e.target.value })}
                    placeholder="0"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12px] font-medium text-ink-soft">PF %</span>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={form.pfPercent}
                    onChange={(e) => setForm({ ...form, pfPercent: e.target.value })}
                    placeholder="12"
                  />
                </label>
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-soft">From month</span>
                <Input
                  type="month"
                  max={CURRENT_MONTH}
                  value={form.fromMonth}
                  onChange={(e) => setForm({ ...form, fromMonth: e.target.value })}
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-soft">To month</span>
                <Input
                  type="month"
                  max={CURRENT_MONTH}
                  value={form.toMonth}
                  onChange={(e) => setForm({ ...form, toMonth: e.target.value })}
                />
              </label>
            </div>

            <p className="mt-2.5 text-[11.5px] leading-relaxed text-ink-soft">
              GST is added on top of the monthly amount; PF is withheld. One payslip per month
              in the range, up to the current month — regenerating a month replaces it rather
              than adding a duplicate.
            </p>

            <Button className="mt-3" onClick={() => void generate()} disabled={busy}>
              <Plus className="mr-1.5 size-3.5" />
              {busy ? 'Generating…' : 'Generate payslips'}
            </Button>
          </Panel>
        )}

        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : grouped.length === 0 ? (
          <Panel>
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <FileText className="size-6 text-ink-faint" strokeWidth={1.6} />
              <p className="text-[13.5px] font-medium text-ink">No payslips yet</p>
              <p className="text-[12.5px] text-ink-soft">
                {manages
                  ? 'Generate the first run above.'
                  : 'Nothing has been issued to you yet.'}
              </p>
            </div>
          </Panel>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-[12px] border border-line bg-paper px-3 py-2">
              <label className="flex cursor-pointer items-center gap-2 text-[13px] text-ink-soft">
                <input
                  type="checkbox"
                  className="size-3.5 accent-[var(--brand)]"
                  checked={allChosen}
                  onChange={(e) => toggleMany(allIds, e.target.checked)}
                />
                Select all ({allIds.length})
              </label>

              <div className="flex items-center gap-2">
                {selected.size > 0 && (
                  <button
                    className="text-[12.5px] text-ink-ghost underline-offset-2 hover:underline"
                    onClick={() => setSelected(new Set())}
                  >
                    Clear
                  </button>
                )}
                <Button size="sm" disabled={selected.size === 0} onClick={() => window.print()}>
                  <Download className="mr-1.5 size-3" />
                  Download {selected.size || ''} as one PDF
                </Button>
              </div>
            </div>

            {grouped.map(({ person, slips }) => (
            <Panel key={person.id}>
              <div className="flex items-baseline justify-between gap-2">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-3.5 accent-[var(--brand)]"
                    checked={slips.every((x) => selected.has(x.id))}
                    onChange={(e) => toggleMany(slips.map((x) => x.id), e.target.checked)}
                  />
                  <SectionLabel>{person.name ?? person.email}</SectionLabel>
                </label>
                <span className="text-[11.5px] text-ink-ghost">
                  {person.title || describeRole(person.orgRole)}
                </span>
              </div>

              <div className="mt-2 overflow-x-auto">
                <table className="w-full min-w-[520px] border-collapse text-[13px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-ink-ghost">
                      <th className="w-6 py-1.5 pr-2 font-medium"> </th>
                      <th className="py-1.5 pr-3 font-medium">Month</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Gross</th>
                      <th className="py-1.5 pr-3 text-right font-medium">GST</th>
                      <th className="py-1.5 pr-3 text-right font-medium">PF</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Net</th>
                      <th className="py-1.5 text-right font-medium">·</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slips.map((slip) => (
                      <tr key={slip.id} className="border-t border-line">
                        <td className="py-2 pr-2">
                          <input
                            type="checkbox"
                            className="size-3.5 accent-[var(--brand)]"
                            checked={selected.has(slip.id)}
                            onChange={(e) => toggleOne(slip.id, e.target.checked)}
                          />
                        </td>
                        <td className="py-2 pr-3 text-ink">{monthLabel(slip.month)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-ink-soft">
                          {money(slip.grossMinor, slip.currency)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-ink-soft">
                          {slip.gstMinor ? money(slip.gstMinor, slip.currency) : '—'}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-ink-soft">
                          {slip.pfMinor ? `−${money(slip.pfMinor, slip.currency)}` : '—'}
                        </td>
                        <td className="py-2 pr-3 text-right font-semibold tabular-nums text-ink">
                          {money(slip.netMinor, slip.currency)}
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              title="Open a printable copy"
                              onClick={() => window.open(`/payslips/${slip.id}`, '_blank')}
                            >
                              <Download className="size-3" />
                            </Button>
                            {manages && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2"
                                title="Delete"
                                onClick={() => void remove(slip)}
                              >
                                <Trash2 className="size-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            ))}
          </>
        )}

        <p className="flex items-center gap-1.5 text-[11.5px] text-ink-ghost">
          <IndianRupee className="size-3" strokeWidth={2} />
          Figures are stored to the paisa. Payslips are visible to the Company Admin, any Head
          granted access, and the person they belong to.
        </p>
      </div>

      {/* Print-only. `print:hidden` on the interactive page above means a browser
          print renders exactly the ticked payslips and nothing else — that is
          what makes "one PDF" work without a PDF library. */}
      <div className="hidden print:block">
        {chosen.map((slip, i) => (
          <div
            key={slip.id}
            style={{ breakAfter: i === chosen.length - 1 ? 'auto' : 'page' }}
            className="mb-8"
          >
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <div className="text-[17px] font-semibold text-ink">{companyName}</div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-ink-ghost">
                  Payslip
                </div>
              </div>
              <div className="text-[13px] text-ink-soft">{monthLabel(slip.month)}</div>
            </div>

            <div className="mt-4 border-t border-line pt-3">
              <div className="text-[14px] font-medium text-ink">
                {slip.user.name ?? slip.user.email}
              </div>
              <div className="text-[12.5px] text-ink-soft">{slip.user.title}</div>
              <div className="text-[12.5px] text-ink-ghost">{slip.user.email}</div>
            </div>

            <table className="mt-4 w-full border-collapse text-[13px]">
              <tbody>
                <tr className="border-t border-line">
                  <td className="py-1.5 text-ink-soft">Gross pay</td>
                  <td className="py-1.5 text-right tabular-nums text-ink-soft">
                    {money(slip.grossMinor, slip.currency)}
                  </td>
                </tr>
                {slip.gstMinor > 0 && (
                  <tr className="border-t border-line">
                    <td className="py-1.5 text-ink-soft">GST</td>
                    <td className="py-1.5 text-right tabular-nums text-ink-soft">
                      + {money(slip.gstMinor, slip.currency)}
                    </td>
                  </tr>
                )}
                {slip.pfMinor > 0 && (
                  <tr className="border-t border-line">
                    <td className="py-1.5 text-ink-soft">Provident fund</td>
                    <td className="py-1.5 text-right tabular-nums text-ink-soft">
                      − {money(slip.pfMinor, slip.currency)}
                    </td>
                  </tr>
                )}
                <tr className="border-t-2 border-ink/20">
                  <td className="py-1.5 font-semibold text-ink">Net pay</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums text-ink">
                    {money(slip.netMinor, slip.currency)}
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="mt-3 text-[10.5px] text-ink-ghost">
              Computer-generated payslip — no signature required.
            </p>
          </div>
        ))}
      </div>
    </AppShell>
  );
}

export default function Payslips() {
  return (
    <AuthGuard>
      <PayslipsPage />
    </AuthGuard>
  );
}
